import { pose as poseOps, type Pose } from '../math/pose'
import { OneEuroFilter, OneEuroVector, type OneEuroParams } from './one-euro'
import { OneEuroQuat } from './quat-filter'

export interface PoseSmootherConfig {
  position: Partial<OneEuroParams>
  rotation: Partial<OneEuroParams>
  scale: Partial<OneEuroParams>
}

/**
 * Presets tuned for face-anchored wearables at ~30 fps with MediaPipe jitter
 * (positions in cm, rotations in rad).
 */
export const POSE_SMOOTHER_PRESETS: Record<'stable' | 'balanced' | 'responsive' | 'off', PoseSmootherConfig> = {
  stable: {
    position: { minCutoff: 0.8, beta: 0.02, dCutoff: 1.0 },
    rotation: { minCutoff: 0.8, beta: 0.05, dCutoff: 1.0 },
    scale: { minCutoff: 0.5, beta: 0.0, dCutoff: 1.0 },
  },
  balanced: {
    position: { minCutoff: 1.5, beta: 0.05, dCutoff: 1.0 },
    rotation: { minCutoff: 1.5, beta: 0.15, dCutoff: 1.0 },
    scale: { minCutoff: 1.0, beta: 0.0, dCutoff: 1.0 },
  },
  responsive: {
    position: { minCutoff: 3.0, beta: 0.2, dCutoff: 1.0 },
    rotation: { minCutoff: 3.0, beta: 0.5, dCutoff: 1.0 },
    scale: { minCutoff: 2.0, beta: 0.0, dCutoff: 1.0 },
  },
  off: {
    position: { minCutoff: 1e6, beta: 0, dCutoff: 1.0 },
    rotation: { minCutoff: 1e6, beta: 0, dCutoff: 1.0 },
    scale: { minCutoff: 1e6, beta: 0, dCutoff: 1.0 },
  },
}

/** Smooths a Pose stream (position One-Euro ×3, rotation One-Euro-on-SO(3), scale One-Euro). */
export class PoseSmoother {
  private pos: OneEuroVector
  private rot: OneEuroQuat
  private scl: OneEuroFilter
  private out: Pose = poseOps.create()
  private _config: PoseSmootherConfig

  constructor(config: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS = 'balanced') {
    this._config = typeof config === 'string' ? POSE_SMOOTHER_PRESETS[config] : config
    this.pos = new OneEuroVector(3, this._config.position)
    this.rot = new OneEuroQuat(this._config.rotation)
    this.scl = new OneEuroFilter(this._config.scale)
  }

  get config(): PoseSmootherConfig {
    return this._config
  }

  /** Replace filter parameters (resets state). */
  configure(config: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this._config = typeof config === 'string' ? POSE_SMOOTHER_PRESETS[config] : config
    this.pos = new OneEuroVector(3, this._config.position)
    this.rot = new OneEuroQuat(this._config.rotation)
    this.scl = new OneEuroFilter(this._config.scale)
  }

  /**
   * Feed a raw pose; returns the smoothed pose (internally owned object — copy if you keep it).
   * @param tMs timestamp in milliseconds
   */
  update(raw: Pose, tMs: number): Pose {
    const t = tMs / 1000
    this.pos.filter(this.out.position, raw.position, t)
    this.rot.filter(this.out.rotation, raw.rotation, t)
    this.out.scale = this.scl.filter(raw.scale, t)
    return this.out
  }

  /** Last smoothed pose (or null before the first update). */
  get value(): Pose | null {
    return this.rot.value ? this.out : null
  }

  reset(): void {
    this.pos.reset()
    this.rot.reset()
    this.scl.reset()
  }
}
